import { useEffect, useRef, useState } from "react";
import { ArrowUpIcon, ClockIcon } from "lucide-react";

import { Column, Row, Text } from "@/components";
import { SadieSimulatorEnvelopeChips } from "@/components/tools/sadie-simulator/SadieSimulatorEnvelopeChips";
import { SadieSimulatorSearchNote } from "@/components/tools/sadie-simulator/SadieSimulatorSearchNote";
import { SadieSimulatorTypingDots } from "@/components/tools/sadie-simulator/SadieSimulatorTypingDots";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

import { usePieceReveal } from "@/hooks/usePieceReveal";
import { sadieSimulator } from "@/lib/sadieSimulator";
import { cn } from "@/lib/utils";
import { SadieSimulatorEntry } from "@/types";

// The phone view draws the thread the way the worker would see it: grey
// bubbles from the agent on the left, blue bubbles from the worker on the
// right. Only the envelope chips give away that it is a simulation.
export const MobileSadieSimulatorConversation = ({
  agentName,
  entries,
  isEnded,
  isFollowUpAvailable,
  isHandoffAvailable,
  isHandoffPending,
  isLoopedToSession1,
  isPending,
  isSetupLoading,
  onContinueToPulse,
  onFollowUp,
  onSend,
  onStart,
  priorEntries,
  workerTextsFirst,
}: {
  agentName: string;
  entries: SadieSimulatorEntry[];
  isEnded: boolean;
  // A pulse opener is waiting on a reply and the one follow-up has not gone
  // out yet, the same conditions production sends it under.
  isFollowUpAvailable: boolean;
  // Session 1 has resolved and can be handed into Session 2.
  isHandoffAvailable: boolean;
  isHandoffPending: boolean;
  // Session 3 set loopedToSession, so production hands the thread to
  // Session 1 here and the simulator stops.
  isLoopedToSession1: boolean;
  isPending: boolean;
  // The prompt is being re-rendered after a setup change. Kept apart from
  // isPending so the typing dots do not show for it.
  isSetupLoading: boolean;
  onContinueToPulse: () => void;
  onFollowUp: () => void;
  onSend: (text: string) => void;
  onStart: () => void;
  // The Session 1 thread a running Session 2 was handed off from. Shown
  // dimmed above the live thread, never sent to the agent.
  priorEntries: SadieSimulatorEntry[];
  // Session 3: there is no opener, the worker's text starts the thread.
  workerTextsFirst: boolean;
}) => {
  /* React Hooks */
  const [draft, setDraft] = useState("");
  const bottomRef = useRef<HTMLDivElement>(null);
  const draftRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [entries.length, isPending, priorEntries.length]);

  /* Helpers */
  const isStarted = entries.length > 0;
  // Typing stays open while a turn is in flight, the way a chat does. Only
  // sending waits. Disabling the box would also drop focus on every send.
  const isInputDisabled = (!isStarted && !workerTextsFirst) || isEnded;
  const isSendDisabled = isInputDisabled || isPending || isSetupLoading;

  // A reset empties the transcript. A half-typed reply should not outlive it.
  // Once the opener lands, the box is ready to type into.
  useEffect(() => {
    if (!isStarted) {
      setDraft("");
      return;
    }
    draftRef.current?.focus();
  }, [isStarted]);

  const inputPlaceholder = () => {
    if (isEnded) return "Conversation ended";
    if (isStarted) return "Text Message";
    if (workerTextsFirst) return "Text in as the worker";
    return "Send the opener first";
  };

  /* Handlers */
  const handleSubmit = () => {
    const text = draft.trim();
    if (!text || isSendDisabled) return;
    onSend(text);
    setDraft("");
  };

  /* Render */
  return (
    <Column className="min-h-0 flex-1 bg-white">
      <div className="min-h-0 flex-1 overflow-y-auto">
        <Column className="min-h-full gap-2 px-4 py-4">
          {priorEntries.length > 0 ? (
            <Column className="gap-2">
              <Column className="gap-2 opacity-50">
                {priorEntries.map((entry, i) => (
                  <MobileSimulatorMessage
                    animate={false}
                    entry={entry}
                    key={i}
                  />
                ))}
              </Column>
              <Text className="py-2 text-center text-xs font-normal text-slate-400">
                Session 1 ended. Sadie carries a digest of it forward, not the
                messages.
              </Text>
            </Column>
          ) : null}
          {/* After a handoff this sits under the Session 1 thread, so the
              next step is where the eye already is. */}
          {!isStarted && !isPending ? (
            <Column className="m-auto items-center gap-4 text-center">
              {workerTextsFirst ? (
                <Text className="text-sm text-slate-500">
                  The worker texts first. Send their message below.
                </Text>
              ) : (
                <>
                  <Text className="text-sm text-slate-500">
                    {agentName} sends the first message. Then you reply as the
                    worker.
                  </Text>
                  <Button disabled={isSetupLoading} onClick={onStart}>
                    Send the opener
                  </Button>
                </>
              )}
            </Column>
          ) : null}
          {entries.map((entry, i) => (
            <MobileSimulatorMessage
              animate={i === entries.length - 1}
              entry={entry}
              key={i}
            />
          ))}
          {isPending ? (
            <SadieSimulatorTypingDots className="rounded-2xl bg-slate-100 px-4 py-3" />
          ) : null}
          <div ref={bottomRef} />
        </Column>
      </div>

      {isLoopedToSession1 ? (
        <Text className="bg-white px-3 py-2 text-center text-xs font-normal text-slate-400">
          Production hands this conversation to Session 1 here. The simulator
          stops.
        </Text>
      ) : null}
      {isHandoffAvailable ? (
        <Column className="items-center gap-1 bg-white px-3 py-2">
          <Button
            disabled={isHandoffPending || isSetupLoading}
            onClick={onContinueToPulse}
            size="sm"
          >
            {isHandoffPending
              ? "Writing the digest"
              : "Continue into Session 2"}
          </Button>
          <Text className="text-center text-xs font-normal text-slate-400">
            The digest agent reads this conversation. Session 2 opens on what it
            kept.
          </Text>
        </Column>
      ) : null}
      {isFollowUpAvailable ? (
        <Row className="justify-center bg-white px-3 py-2">
          <Button
            className="h-7 gap-1.5 rounded-full border border-dashed border-slate-300 bg-white px-3 text-xs font-medium text-slate-500 shadow-none hover:border-slate-400 hover:bg-slate-50 hover:text-slate-700"
            disabled={isPending || isSetupLoading}
            onClick={onFollowUp}
            size="sm"
            variant="ghost"
          >
            <ClockIcon className="h-3.5 w-3.5" />A day passes with no reply.
            Send the follow-up
          </Button>
        </Row>
      ) : null}
      <Row className="items-end gap-2 border-t border-slate-200 bg-white px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-2">
        <Textarea
          autoExpand
          // 16px keeps iOS from zooming the page when the box is focused.
          className="max-h-32 min-h-0 resize-none rounded-[20px] border-slate-300 px-4 py-2 text-base leading-snug md:text-base"
          disabled={isInputDisabled}
          onChange={(e) => {
            setDraft(e.target.value);
          }}
          placeholder={inputPlaceholder()}
          ref={draftRef}
          rows={1}
          value={draft}
        />
        <Button
          aria-label="Send"
          className="h-9 w-9 shrink-0 rounded-full"
          disabled={isSendDisabled || draft.trim().length === 0}
          onClick={handleSubmit}
          size="icon"
        >
          <ArrowUpIcon className="h-4 w-4" />
        </Button>
      </Row>
    </Column>
  );
};

const MobileSimulatorMessage = ({
  animate,
  entry,
}: {
  animate: boolean;
  entry: SadieSimulatorEntry;
}) => {
  const isAgent = entry.role === "assistant";
  const pieces = isAgent
    ? sadieSimulator.splitPieces(entry.content)
    : [entry.content];
  const isSilent = isAgent && pieces.length === 0;
  const { revealed, revealAll } = usePieceReveal(
    pieces.length,
    animate && isAgent,
  );
  const lastPieceRef = useRef<HTMLDivElement>(null);
  const isComplete = revealed >= pieces.length;

  useEffect(() => {
    lastPieceRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [revealed]);

  const bubbleClassName = cn(
    "whitespace-pre-wrap rounded-[18px] px-3.5 py-2 text-base leading-snug",
    isAgent ? "bg-slate-100 text-slate-900" : "bg-primary-600 text-white",
  );

  return (
    <Column
      className={cn("max-w-[82%] gap-1", isAgent ? "self-start" : "self-end")}
      onClick={revealAll}
    >
      {entry.search ? (
        <SadieSimulatorSearchNote className="pb-1" search={entry.search} />
      ) : null}
      {isSilent ? (
        <Text className="text-sm italic text-slate-400">
          {"(nothing sent)"}
        </Text>
      ) : null}
      {pieces.slice(0, revealed).map((piece, i) => (
        <div
          className={cn(bubbleClassName, !isAgent && "self-end")}
          key={i}
          ref={i === revealed - 1 ? lastPieceRef : undefined}
        >
          {piece}
        </div>
      ))}
      {!isComplete ? (
        <SadieSimulatorTypingDots className="rounded-2xl bg-slate-100 px-4 py-3" />
      ) : null}
      {entry.turn && isComplete ? (
        <SadieSimulatorEnvelopeChips
          className="pl-1 pt-0.5"
          turn={entry.turn}
        />
      ) : null}
    </Column>
  );
};
